import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { store, useApp } from '../state/store.ts';
import { formatElapsed, relativeTime } from '../lib/format.ts';
import { describePerson } from '../lib/people.ts';
import { Avatar, useNow } from './common.tsx';
import { HoursSheet } from './HoursSheet.tsx';

const FOUR_HOURS = 4 * 3600;

function SyncIndicator() {
  const { syncStatus, syncing, online } = useApp();
  useNow(30_000);
  const failed = syncStatus?.last_run?.status === 'error';
  return (
    <div className="sync" aria-live="polite">
      <span className="sync-text" data-testid="sync-indicator">
        {syncing ? 'Actualizando…' : `Actualizado ${relativeTime(syncStatus?.last_success_at)}`}
      </span>
      <button
        type="button"
        className="icon-button"
        aria-label="Actualizar"
        title="Actualizar"
        disabled={syncing || !online}
        onClick={() => void store.refresh('boton')}
      >
        ⟳
      </button>
      {failed && !syncing && (
        <p className="banner banner-error" role="alert">
          La última sincronización con Notion falló. Se muestran los datos guardados.
        </p>
      )}
    </div>
  );
}

/** Botón con las iniciales: Mis tareas, Hoy, Todas las tarjetas, Reportes, Buscar y Salir. */
function AccountMenu() {
  const { profile, email } = useApp();
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!profile) return null;
  const person = describePerson(profile.id, null, profile);
  return (
    <div className="account" ref={root}>
      <button
        type="button"
        className="account-button"
        aria-label="Menú de la cuenta"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(!open)}
      >
        <Avatar person={person} decorative />
      </button>
      {open && (
        <nav
          id={menuId}
          className="account-menu"
          aria-label="Cuenta"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('a')) setOpen(false);
          }}
        >
          <p className="account-name">
            {person.label}
            <span className="muted small">{email ?? profile.email}</span>
          </p>
          <NavLink to="/" end>
            Mis tareas
          </NavLink>
          <NavLink to="/hoy">Hoy</NavLink>
          <NavLink to="/todas">Todas las tarjetas</NavLink>
          <NavLink to="/reportes">Reportes</NavLink>
          <NavLink to="/buscar">Buscar cerradas</NavLink>
          <button type="button" className="link-button" onClick={() => void store.signOut()}>
            Salir
          </button>
        </nav>
      )}
    </div>
  );
}

function TimerBar() {
  const { openEntry, cards } = useApp();
  const now = useNow(1000);
  if (!openEntry) return null;
  const card = cards.find((c) => c.id === openEntry.card_id);
  const elapsed = (now.getTime() - Date.parse(openEntry.started_at)) / 1000;
  const overFour = elapsed >= FOUR_HOURS || Boolean(openEntry.warned_at);
  return (
    <div
      className={`timer-bar${overFour ? ' timer-bar-warn' : ''}`}
      role="status"
      data-testid="timer-bar"
    >
      <span className="timer-dot" aria-hidden="true" />
      <Link to={`/tarjeta/${openEntry.card_id}`} className="timer-card">
        {card?.name ?? 'Tarjeta'}
      </Link>
      <span className="timer-elapsed">{formatElapsed(elapsed)}</span>
      <button type="button" className="button button-small" onClick={() => void store.stopTimer()}>
        Detener
      </button>
      {overFour && (
        <p className="timer-warning">
          El cronómetro lleva más de 4 horas. Se cerrará solo a las 8 horas y quedará para revisión.
        </p>
      )}
    </div>
  );
}

function Toasts() {
  const { toasts } = useApp();
  return (
    <div className="toasts" aria-live="assertive">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast toast-${t.kind}`}
          role={t.kind === 'error' ? 'alert' : 'status'}
        >
          <span>{t.message}</span>
          {t.action && (
            <button
              type="button"
              className="toast-action"
              onClick={() => {
                t.action?.run();
                store.dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            className="icon-button"
            aria-label="Cerrar aviso"
            onClick={() => store.dismissToast(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { online, pending } = useApp();
  // Mis tareas muestra el cronómetro en curso en su franja «Trabajando en».
  const { pathname } = useLocation();
  return (
    <div className={`app${pathname === '/' || pathname === '/todas' ? ' app-board' : ''}`}>
      <header className="topbar">
        <Link to="/" className="brand">
          Tablero
        </Link>
        <div className="topbar-end">
          <SyncIndicator />
          <AccountMenu />
        </div>
      </header>
      {!online && (
        <p className="banner banner-offline" role="status">
          Sin conexión. Se muestra la última copia guardada; los cambios se aplicarán al reconectar.
        </p>
      )}
      {pending > 0 && (
        <p className="banner banner-pending" role="status" data-testid="pending">
          {pending === 1 ? '1 cambio pendiente' : `${pending} cambios pendientes`} de sincronizar
        </p>
      )}
      {pathname !== '/' && <TimerBar />}
      <main className="content">{children}</main>
      <HoursSheet />
      <Toasts />
    </div>
  );
}
